import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { OptionsAnalyticsMetricInfoDialog } from './OptionsAnalyticsMetricInfoDialog';
import { buildAnalyticsMetricInfoMap } from './optionsAnalyticsMetricInfo';
import {
  buildAnalyticsValueDisplay,
  formatAnalyticsDays,
  formatAnalyticsRatio,
  getRatioTone,
} from './optionsAnalyticsUtils';
import { formatDate, formatStrategyLabel } from './optionsHistoryUtils';
import { formatPercent, formatSignedNative, formatSignedUsd, getCurrencyDigits, getValueTone } from './optionsPerformanceUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

const LabelWithInfo = ({ label, info, testId }) => {
  if (!info) {
    return <p className="text-[10px] uppercase tracking-[0.18em] text-white/38" data-typography="label" data-testid={testId}>{label}</p>;
  }

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/38" data-typography="label">{label}</p>
      <OptionsAnalyticsMetricInfoDialog info={info} testId={`${testId}-dialog`} />
    </div>
  );
};

const StatCard = ({ card, testId }) => (
  <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={testId}>
    <CardContent className="px-4 py-4">
      <LabelWithInfo label={card.label} info={card.info} testId={`${testId}-label`} />
      <p className={`mt-2 text-2xl font-black tracking-tight ${card.toneClass}`} data-typography="metric" data-testid={`${testId}-value`}>{card.primary}</p>
      {card.secondary ? <p className="mt-1 text-xs text-white/52 tabular-nums" data-testid={`${testId}-secondary`}>{card.secondary}</p> : null}
    </CardContent>
  </Card>
);

const DetailCard = ({ title, rows, testId }) => (
  <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={testId}>
    <CardContent className="space-y-3 px-4 py-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/38" data-typography="label" data-testid={`${testId}-title`}>{title}</p>
      <div className="space-y-2" data-testid={`${testId}-rows`}>
        {rows.map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-3" data-testid={`${testId}-row-${row.key}`}>
            <p className="text-xs uppercase tracking-[0.16em] text-white/45" data-typography="label" data-testid={`${testId}-row-${row.key}-label`}>{row.label}</p>
            <div className="text-right">
              <p className={`text-sm font-semibold tabular-nums ${row.toneClass}`} data-typography="metric" data-testid={`${testId}-row-${row.key}-value`}>{row.value}</p>
              {row.secondary ? <p className="mt-1 text-[11px] text-white/52" data-testid={`${testId}-row-${row.key}-secondary`}>{row.secondary}</p> : null}
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const buildTradeDisplay = ({ trade, selectedCurrency }) => {
  if (!trade) {
    return { primary: '—', secondary: null, toneClass: 'text-white' };
  }

  const requestedCurrency = String(selectedCurrency || 'usd').toUpperCase();
  const tradeCurrency = String(trade.underlying || '').toUpperCase();
  if (requestedCurrency !== 'USD' && tradeCurrency === requestedCurrency && trade.net_pnl_base !== null && trade.net_pnl_base !== undefined) {
    return {
      primary: formatSignedNative(trade.net_pnl_base, tradeCurrency, getCurrencyDigits(tradeCurrency)),
      secondary: trade.net_pnl_usd !== null && trade.net_pnl_usd !== undefined ? `≈ ${formatSignedUsd(trade.net_pnl_usd)}` : null,
      toneClass: getValueTone(trade.net_pnl_base),
    };
  }

  return {
    primary: formatSignedUsd(trade.net_pnl_usd),
    secondary: tradeCurrency && trade.net_pnl_base !== null && trade.net_pnl_base !== undefined
      ? `${tradeCurrency} ${formatSignedNative(trade.net_pnl_base, tradeCurrency, getCurrencyDigits(tradeCurrency))}`
      : null,
    toneClass: getValueTone(trade.net_pnl_usd),
  };
};

const TradeHighlightCard = ({ title, trade, selectedCurrency, language, t, testId }) => {
  const pnlDisplay = buildTradeDisplay({ trade, selectedCurrency });

  return (
    <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={testId}>
      <CardContent className="space-y-3 px-4 py-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/38" data-typography="label" data-testid={`${testId}-title`}>{title}</p>
        {trade ? (
          <>
            <div className="flex items-center gap-3" data-testid={`${testId}-headline`}>
              <OptionsAssetIcon assetKey={trade.underlying} size={20} />
              <div>
                <p className="text-base font-semibold text-white" data-testid={`${testId}-strategy`}>{formatStrategyLabel(trade.strategy_label)}</p>
                <p className="text-xs uppercase tracking-[0.16em] text-white/42" data-testid={`${testId}-underlying`}>{trade.underlying}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-3" data-testid={`${testId}-pnl-block`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/38" data-testid={`${testId}-pnl-label`}>{t('analytics.tradingPnl')}</p>
              <p className={`mt-1 text-lg font-bold tabular-nums ${pnlDisplay.toneClass}`} data-typography="metric" data-testid={`${testId}-pnl-value`}>{pnlDisplay.primary}</p>
              {pnlDisplay.secondary ? <p className="mt-1 text-xs text-white/52" data-testid={`${testId}-pnl-secondary`}>{pnlDisplay.secondary}</p> : null}
            </div>
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-3" data-testid={`${testId}-date-row`}>
              <p className="text-xs uppercase tracking-[0.16em] text-white/45" data-typography="label" data-testid={`${testId}-date-label`}>{t('analytics.closedAt')}</p>
              <p className="text-sm font-semibold text-white" data-typography="metric" data-testid={`${testId}-date-value`}>{formatDate(trade.closed_at, language)}</p>
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-4 text-sm text-white/52" data-testid={`${testId}-empty`}>
            {t('analytics.notEnoughData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const buildOperationProfileRows = ({ metrics, performanceQuality, selectedCurrency, t }) => {
  const averageWinnerDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'average_winner_usd', nativeField: 'average_winner_native' });
  const averageLoserDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'average_loser_usd', nativeField: 'average_loser_native' });

  return [
    {
      key: 'average-winner',
      label: t('analytics.averageWinner'),
      value: averageWinnerDisplay.primary,
      secondary: averageWinnerDisplay.secondary,
      toneClass: averageWinnerDisplay.toneClass,
    },
    {
      key: 'average-loser',
      label: t('analytics.averageLoser'),
      value: averageLoserDisplay.primary,
      secondary: averageLoserDisplay.secondary,
      toneClass: averageLoserDisplay.toneClass,
    },
    {
      key: 'win-streak',
      label: t('analytics.longestWinStreak'),
      value: String(performanceQuality?.longest_win_streak || 0),
      secondary: null,
      toneClass: 'text-[#22C55E]',
    },
    {
      key: 'loss-streak',
      label: t('analytics.longestLossStreak'),
      value: String(performanceQuality?.longest_loss_streak || 0),
      secondary: null,
      toneClass: 'text-[#EF4444]',
    },
  ];
};

const buildEntryVsExitDteRows = ({ metrics, t }) => ([
  { key: 'entry-dte', label: t('analytics.averageEntryDte'), value: formatAnalyticsDays(metrics?.average_entry_dte), secondary: null, toneClass: 'text-white' },
  { key: 'exit-dte', label: t('analytics.averageExitDte'), value: formatAnalyticsDays(metrics?.average_exit_dte), secondary: null, toneClass: 'text-white' },
  { key: 'dte-reduction', label: t('analytics.dteReduction'), value: formatAnalyticsDays(metrics?.average_dte_reduction), secondary: null, toneClass: getValueTone(metrics?.average_dte_reduction) },
]);

export const OptionsAnalyticsTradeResultsSection = ({ performanceQuality, selectedCurrency }) => {
  const { t, language } = useOptionsLocale();
  const metrics = performanceQuality?.metrics;
  const profileRows = buildOperationProfileRows({ metrics, performanceQuality, selectedCurrency, t });

  return (
    <div className="space-y-3" data-testid="options-analytics-trade-results-section">
      <div className="grid gap-3 xl:grid-cols-2" data-testid="options-analytics-trade-results-grid">
        <DetailCard title={t('analytics.operationProfile')} rows={profileRows} testId="options-analytics-trade-results-profile" />
        <div className="space-y-3" data-testid="options-analytics-trade-results-highlights-stack">
          <TradeHighlightCard title={t('analytics.bestOperation')} trade={performanceQuality?.best_trade} selectedCurrency={selectedCurrency} language={language} t={t} testId="options-analytics-trade-results-best-trade" />
          <TradeHighlightCard title={t('analytics.worstOperation')} trade={performanceQuality?.worst_trade} selectedCurrency={selectedCurrency} language={language} t={t} testId="options-analytics-trade-results-worst-trade" />
        </div>
      </div>
    </div>
  );
};

export const OptionsAnalyticsEntryVsExitDteSection = ({ performanceQuality }) => {
  const { t } = useOptionsLocale();
  const metrics = performanceQuality?.metrics;
  const dteRows = buildEntryVsExitDteRows({ metrics, t });

  return (
    <div className="space-y-3" data-testid="options-analytics-entry-exit-dte-section">
      <DetailCard title={t('analytics.entryVsExitDte')} rows={dteRows} testId="options-analytics-entry-exit-dte-card" />
    </div>
  );
};

export const OptionsAnalyticsPerformanceQualitySection = ({ performanceQuality, selectedCurrency }) => {
  const { t, formatMessage } = useOptionsLocale();
  const metrics = performanceQuality?.metrics;
  const drawdownPctDisplay = performanceQuality?.max_drawdown_pct === null || performanceQuality?.max_drawdown_pct === undefined
    ? null
    : formatPercent(performanceQuality.max_drawdown_pct);
  const feesDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'fees_usd', nativeField: 'fees_native', absolute: true });
  const metricInfoMap = buildAnalyticsMetricInfoMap({ metrics, performanceQuality, selectedCurrency, t, formatMessage });

  const maxDrawdownDisplay = selectedCurrency === 'usd'
    ? {
      primary: formatSignedUsd(performanceQuality?.max_drawdown_usd),
      secondary: drawdownPctDisplay,
      toneClass: getValueTone(performanceQuality?.max_drawdown_usd),
    }
    : {
      primary: performanceQuality?.max_drawdown_native === null || performanceQuality?.max_drawdown_native === undefined
        ? '—'
        : formatSignedNative(performanceQuality.max_drawdown_native, selectedCurrency.toUpperCase(), getCurrencyDigits(selectedCurrency.toUpperCase())),
      secondary: [
        drawdownPctDisplay,
        performanceQuality?.max_drawdown_usd !== null && performanceQuality?.max_drawdown_usd !== undefined ? `≈ ${formatSignedUsd(performanceQuality.max_drawdown_usd)}` : null,
      ].filter(Boolean).join(' · ') || null,
      toneClass: getValueTone(performanceQuality?.max_drawdown_native),
    };

  const statCards = [
    {
      key: 'profit-factor',
      label: t('analytics.profitFactor'),
      primary: formatAnalyticsRatio(metrics?.profit_factor),
      secondary: null,
      toneClass: getRatioTone(metrics?.profit_factor),
      info: metricInfoMap.profitFactor,
    },
    {
      key: 'payoff-ratio',
      label: t('analytics.payoffRatio'),
      primary: formatAnalyticsRatio(metrics?.payoff_ratio),
      secondary: null,
      toneClass: getRatioTone(metrics?.payoff_ratio),
      info: metricInfoMap.payoffRatio,
    },
    {
      key: 'expectancy',
      label: t('analytics.expectancy'),
      info: metricInfoMap.expectancy,
      ...buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'expectancy_usd', nativeField: 'expectancy_native' }),
    },
    {
      key: 'avg-win',
      label: t('analytics.averageWinner'),
      ...buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'average_winner_usd', nativeField: 'average_winner_native' }),
    },
    {
      key: 'avg-loss',
      label: t('analytics.averageLoser'),
      ...buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'average_loser_usd', nativeField: 'average_loser_native' }),
    },
  ];

  return (
    <section className="space-y-3" data-testid="options-analytics-performance-quality-section">
      <div className="space-y-1" data-testid="options-analytics-performance-quality-header">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45" data-testid="options-analytics-performance-quality-title">{t('analytics.operationQuality')}</p>
        <p className="text-sm text-white/55" data-testid="options-analytics-performance-quality-helper">{t('analytics.operationQualityHelper')}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="options-analytics-performance-quality-kpis">
        {statCards.map((card) => (
          <StatCard key={card.key} card={card} testId={`options-analytics-performance-quality-card-${card.key}`} />
        ))}
      </div>
    </section>
  );
};

export default OptionsAnalyticsPerformanceQualitySection;